// src/pages/Pricing.js
import { useState } from 'react';
import { Link } from 'react-router-dom';

// LIVE MODE — see docs/superpowers/plans/2026-07-04-stripe-team-payment.md Task 12
// (updated for the Solo tier and the $500 Team price). Each live Payment Link must
// have Advanced options → Metadata set to plan=team / plan=solo so
// functions/stripeWebhook.js can tell them apart.
const STRIPE_TEAM_LINK = 'https://buy.stripe.com/14AcMY4rufqr8fPefL28800';
const STRIPE_SOLO_LINK = 'https://buy.stripe.com/fZu4gse244LN1RrdbH28801';

const TEAM_FEATURES = [
  'Up to 10 team members',
  '6-category, 24-question assessment',
  'Anonymous individual responses',
  'Aggregated team health profile',
  'Radar & bar chart results dashboard',
  'Written feedback compilation',
  'Results available immediately',
];

const CONSULTANT_FEATURES = [
  'Everything in the Team plan',
  'Certified consultant attends your meetings',
  'Live observation using Working Genius framework',
  'Detailed meeting health scores & analysis',
  'Conversation quality & participation metrics',
  'Written action plan with specific next steps',
  'Follow-up session to review findings',
];

const SOLO_FEATURES = [
  '6-category individual assessment',
  'Personal health snapshot',
  'Radar chart of your results',
  'Compare with a single colleague',
];

function Check({ gold, olive }) {
  const color = gold ? '#deba5a' : olive ? '#7a9a56' : '#6b757e';
  const bg = gold ? 'rgba(203,164,58,0.15)' : olive ? 'rgba(95,121,66,0.15)' : 'rgba(107,117,126,0.15)';
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
      <circle cx="8" cy="8" r="8" fill={bg} />
      <path d="M5 8l2.5 2.5L11 5.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Pricing() {
  const [teamSize, setTeamSize] = useState(10);
  const perPerson = (500 / teamSize).toFixed(2);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--navy-deep)', fontFamily: 'var(--font-body)' }}>

      {/* Ambient background */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: `
          radial-gradient(ellipse 55% 45% at 90% 5%, rgba(203,164,58,0.1) 0%, transparent 55%),
          radial-gradient(ellipse 45% 55% at 5% 95%, rgba(95,121,66,0.08) 0%, transparent 55%)
        `,
      }} />

      {/* Nav */}
      <nav style={{
        position: 'relative', zIndex: 10,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '24px 52px', borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Link to="/dashboard">
          <img src="/logo-white.png" alt="Meeting.Training" style={{ height: 32 }} />
        </Link>
        <div style={{ display: 'flex', gap: 28, alignItems: 'center' }}>
          <Link to="/contact" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', textDecoration: 'none', fontWeight: 500 }}>Contact</Link>
          <Link to="/dashboard" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', textDecoration: 'none', fontWeight: 500 }}>Dashboard</Link>
        </div>
      </nav>

      {/* Hero */}
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 860, margin: '0 auto', padding: '72px 52px 52px', textAlign: 'center' }}>
        <div style={{
          display: 'inline-block', fontSize: '0.65rem', letterSpacing: '0.22em',
          textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 700,
          marginBottom: 20, padding: '5px 16px', borderRadius: 100,
          border: '1px solid rgba(203,164,58,0.3)', background: 'rgba(203,164,58,0.07)',
        }}>
          Simple, honest pricing
        </div>
        <h1 style={{
          fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 3.4rem)',
          color: 'white', fontWeight: 400, lineHeight: 1.18, marginBottom: 18,
        }}>
          One assessment can change<br />
          <em style={{ color: 'var(--gold-light)' }}>how your whole team works.</em>
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.42)', fontSize: '1rem', lineHeight: 1.8, maxWidth: 480, margin: '0 auto' }}>
          No subscriptions. No per-seat fees. Pay once, get the clarity your team deserves.
        </p>
      </div>

      {/* Pricing cards */}
      <div style={{
        position: 'relative', zIndex: 1,
        maxWidth: 1100, margin: '0 auto', padding: '0 52px',
        display: 'grid', gridTemplateColumns: '1fr 1.08fr 1fr', gap: 20, alignItems: 'start',
      }}>

        {/* SOLO */}
        <div style={{
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 20, padding: '32px 30px',
        }}>
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: '0.65rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--olive-light)', fontWeight: 700, marginBottom: 12 }}>
              Solo Assessment
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '3rem', color: 'white', fontWeight: 600, lineHeight: 1 }}>$50</span>
              <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem' }}>one time</span>
            </div>
            <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.3)', lineHeight: 1.6 }}>The full individual assessment. Pay once.</div>
          </div>
          <a
            href={STRIPE_SOLO_LINK} target="_blank" rel="noopener noreferrer"
            style={{
              display: 'block', textAlign: 'center', padding: '11px 20px', borderRadius: 9,
              background: 'var(--olive)', border: '1.5px solid var(--olive)',
              color: 'white', fontFamily: 'var(--font-body)',
              fontWeight: 700, fontSize: '0.875rem', textDecoration: 'none', marginBottom: 28,
              letterSpacing: '0.02em', boxShadow: '0 8px 24px rgba(95,121,66,0.3)',
            }}
          >
            Pay $50 — Get started →
          </a>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {SOLO_FEATURES.map(f => (
              <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Check olive />
                <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.5 }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* TEAM — featured */}
        <div style={{
          background: 'linear-gradient(160deg, rgba(30,46,69,0.95) 0%, rgba(20,32,50,0.98) 100%)',
          border: '1.5px solid rgba(203,164,58,0.35)',
          borderRadius: 22, padding: '36px 32px', position: 'relative',
          boxShadow: '0 32px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(203,164,58,0.1)',
        }}>
          <div style={{
            position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
            background: 'var(--gold)', color: 'var(--navy-deep)',
            fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.14em',
            textTransform: 'uppercase', padding: '5px 16px', borderRadius: 100, whiteSpace: 'nowrap',
          }}>
            Most popular
          </div>

          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: '0.65rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 700, marginBottom: 12 }}>
              Team Assessment
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '3.2rem', color: 'white', fontWeight: 600, lineHeight: 1 }}>$500</span>
              <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem' }}>one time</span>
            </div>
            <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>Up to 10 team members. Pay once, no renewal.</div>
          </div>

          {/* Team size slider */}
          <div style={{
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12, padding: '16px 18px', marginBottom: 24,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Team size
              </span>
              <span style={{ fontSize: '0.875rem', color: 'var(--gold-light)', fontWeight: 700 }}>{teamSize} members</span>
            </div>
            <input
              type="range" min={2} max={10} value={teamSize}
              onChange={e => setTeamSize(Number(e.target.value))}
              style={{ width: '100%', accentColor: 'var(--gold)', cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.25)' }}>2</span>
              <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>${perPerson} per person</span>
              <span style={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.25)' }}>10</span>
            </div>
          </div>

          <a
            href={STRIPE_TEAM_LINK} target="_blank" rel="noopener noreferrer"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '14px 22px', borderRadius: 10,
              background: 'var(--gold)', color: 'var(--navy-deep)',
              fontFamily: 'var(--font-body)', fontWeight: 800,
              fontSize: '0.95rem', textDecoration: 'none', letterSpacing: '0.02em',
              marginBottom: 16, boxShadow: '0 8px 24px rgba(203,164,58,0.35)',
            }}
          >
            Pay $500 — Get started →
          </a>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 28 }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <rect width="14" height="14" rx="3" fill="#635BFF" fillOpacity="0.3"/>
              <path d="M4 7l2 2 4-4" stroke="#a8a4ff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.28)', letterSpacing: '0.04em' }}>
              Secured by Stripe · Instant access after payment
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {TEAM_FEATURES.map(f => (
              <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Check gold />
                <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.5 }}>{f}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CONSULTANT */}
        <div style={{
          background: 'rgba(95,121,66,0.06)',
          border: '1px solid rgba(95,121,66,0.2)',
          borderRadius: 20, padding: '32px 30px',
        }}>
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontSize: '0.65rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--olive-light)', fontWeight: 700, marginBottom: 12 }}>
              Professional Engagement
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: '2.6rem', color: 'white', fontWeight: 600, lineHeight: 1 }}>$5,000</span>
            </div>
            <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.6 }}>
              A certified consultant embedded with your team.
            </div>
          </div>
          <Link to="/contact" style={{
            display: 'block', textAlign: 'center', padding: '12px 20px', borderRadius: 9,
            background: 'rgba(95,121,66,0.18)', border: '1.5px solid rgba(95,121,66,0.35)',
            color: 'var(--olive-light)', fontFamily: 'var(--font-body)',
            fontWeight: 700, fontSize: '0.875rem', textDecoration: 'none',
            marginBottom: 28, letterSpacing: '0.02em',
          }}>
            Request a quote →
          </Link>
          <div style={{ fontSize: '0.68rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)', fontWeight: 700, marginBottom: 14 }}>
            What's included
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {CONSULTANT_FEATURES.map(f => (
              <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <Check olive />
                <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 }}>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom section */}
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1100, margin: '64px auto 0', padding: '0 52px 100px' }}>
        <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 52 }} />

        {/* Reassurance row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24, marginBottom: 56 }}>
          {[
            { icon: '⚡', title: 'Results in minutes', body: 'As soon as your team submits, the aggregated results are live. No waiting for a report to be written.' },
            { icon: '🔒', title: 'Fully anonymous', body: 'Individual responses are never shown to leaders. Only the aggregated team picture is visible.' },
            { icon: '↩', title: 'Not satisfied?', body: "If your team experiences any issues with the assessment, we'll make it right — no questions asked." },
          ].map(({ icon, title, body }) => (
            <div key={title} style={{
              padding: '28px', background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16,
            }}>
              <div style={{ fontSize: '1.6rem', marginBottom: 14 }}>{icon}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: 'white', fontWeight: 600, marginBottom: 8, lineHeight: 1.3 }}>{title}</div>
              <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.38)', lineHeight: 1.7 }}>{body}</div>
            </div>
          ))}
        </div>

        {/* CTA footer */}
        <div style={{
          background: 'rgba(203,164,58,0.07)', border: '1px solid rgba(203,164,58,0.18)',
          borderRadius: 20, padding: '44px 52px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap',
        }}>
          <div>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'white', fontWeight: 400, marginBottom: 8, lineHeight: 1.3 }}>
              Not sure which plan is right?
            </h3>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.875rem', lineHeight: 1.7, maxWidth: 420 }}>
              Book a free 20-minute call and we'll tell you exactly what we'd recommend for your team's situation.
            </p>
          </div>
          <Link to="/contact" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '13px 28px', borderRadius: 10,
            background: 'var(--gold)', color: 'var(--navy-deep)',
            fontFamily: 'var(--font-body)', fontWeight: 800,
            fontSize: '0.9rem', textDecoration: 'none', whiteSpace: 'nowrap',
            boxShadow: '0 8px 24px rgba(203,164,58,0.25)',
          }}>
            Talk to us first →
          </Link>
        </div>
      </div>
    </div>
  );
}
